import React, { useState } from 'react';
import { FileSignature, FileWarning, Snowflake, Calculator, Target, Users, User, ChevronDown, ChevronUp } from 'lucide-react';
import { WorkCounts, WorkCounter, WorkChip, META_DIA } from './kanbanWork';
import { ResultadoMesResumo, ResultadoMesDetalhe } from './ResultadoMesFaixa';
import type { EstadoResultadoMes } from './useResultadoMes';

// Barra "Meu Trabalho" do CRM (antes "Meu Trabalho Hoje"): resultado do mês sempre
// visível (jornada do mês — em assinatura, assinados, validação, reprovados, aprovados —
// + comissão garantida) — no centro da linha do título em tela larga (2xl+), numa linha
// própria abaixo dela nas menores — e, ao abrir, os contadores do dia e os chips de filtro.
// No celular (abaixo de md) a barra recolhida é só a linha do título: o resultado do mês
// aparece ao abrir, junto com o resto, para sobrar altura aos cards do Kanban.
interface MeuTrabalhoHojeProps {
    /** Resultado do mês do filtro de responsáveis atual (useResultadoMes). */
    resultado: EstadoResultadoMes;
    counts: WorkCounts;
    activeCounter: WorkCounter | null;
    onToggleCounter: (c: WorkCounter) => void;
    activeChip: WorkChip;
    onSelectChip: (c: WorkChip) => void;
    favoritosCount: number;
    canToggleScope: boolean;
    scope: 'eu' | 'todos';
    onChangeScope: (s: 'eu' | 'todos') => void;
    /** Parceiro/vendedor_parceiro: troca a fileira de chips por um select único, mais simples no mobile */
    simpleChipFilter?: boolean;
}

const CONTADORES: Array<{ key: WorkCounter; label: string; icon: React.ElementType; tone: string; activeTone: string }> = [
    { key: 'termos_pendentes', label: 'Termos pendentes', icon: FileSignature, tone: 'text-amber-600 dark:text-amber-400', activeTone: 'bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700' },
    { key: 'pendencias_documentais', label: 'Pendências documentais', icon: FileWarning, tone: 'text-orange-600 dark:text-orange-400', activeTone: 'bg-orange-50 border-orange-300 dark:bg-orange-900/20 dark:border-orange-700' },
    { key: 'sem_interacao', label: 'Sem interação', icon: Snowflake, tone: 'text-sky-600 dark:text-sky-400', activeTone: 'bg-sky-50 border-sky-300 dark:bg-sky-900/20 dark:border-sky-700' },
    { key: 'simulacoes_hoje', label: 'Simulações hoje', icon: Calculator, tone: 'text-purple-600 dark:text-purple-400', activeTone: 'bg-purple-50 border-purple-300 dark:bg-purple-900/20 dark:border-purple-700' },
];

const CHIPS: Array<{ key: WorkChip; label: string }> = [
    { key: 'todos', label: 'Todos' },
    { key: 'meu_trabalho', label: 'Meu Trabalho Hoje' },
    { key: 'pendencias_documentais', label: 'Pendências Documentais' },
    { key: 'termos_pendentes', label: 'Termos Pendentes' },
    { key: 'sem_interacao', label: 'Sem Interação' },
    { key: 'favoritos', label: 'Favoritos' },
];

const MeuTrabalhoHoje: React.FC<MeuTrabalhoHojeProps> = ({
    resultado, counts, activeCounter, onToggleCounter, activeChip, onSelectChip,
    favoritosCount, canToggleScope, scope, onChangeScope, simpleChipFilter,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [porVendedorAberto, setPorVendedorAberto] = useState(false);
    const togglePorVendedor = () => setPorVendedorAberto(v => !v);
    const meta = Math.min(counts.meta_hoje, META_DIA);
    const metaPct = (meta / META_DIA) * 100;
    const metaAtingida = counts.meta_hoje >= META_DIA;

    const counterValue = (k: WorkCounter) => {
        switch (k) {
            case 'termos_pendentes': return counts.termos_pendentes;
            case 'pendencias_documentais': return counts.pendencias_documentais;
            case 'sem_interacao': return counts.sem_interacao;
            case 'simulacoes_hoje': return counts.simulacoes_hoje;
        }
    };

    // Resultado do mês no centro da linha do título — só em tela larga (2xl+), onde as cinco
    // etapas da jornada cabem sem encostar no "Meu Trabalho" nem nas visitas.
    const resultadoNaLinha = (
        <div className="hidden 2xl:flex flex-1 min-w-0 justify-center px-4">
            <ResultadoMesResumo estado={resultado} variante="linha" porVendedorAberto={porVendedorAberto} onTogglePorVendedor={togglePorVendedor} />
        </div>
    );
    // Nas telas menores ele desce para uma linha própria; a tabela por vendedor
    // (e o aviso de erro) ficam embaixo da barra nos dois casos. No celular, só com a barra aberta.
    const resultadoEmBloco = (
        <div className={isCollapsed ? 'hidden md:block' : ''}>
            <div className="2xl:hidden mt-2.5 pt-2.5 border-t border-gray-50 dark:border-gray-700/50">
                <ResultadoMesResumo estado={resultado} variante="bloco" porVendedorAberto={porVendedorAberto} onTogglePorVendedor={togglePorVendedor} />
            </div>
            <ResultadoMesDetalhe estado={resultado} porVendedorAberto={porVendedorAberto} />
        </div>
    );

    if (simpleChipFilter) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm px-3 py-2 md:px-4 md:py-3 mb-2 md:mb-3 transition-all duration-300">
                {/* Header always visible */}
                <div 
                    className="flex items-center justify-between cursor-pointer select-none"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                >
                    <span className="shrink-0 text-[11px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        Meu Trabalho
                    </span>
                    {resultadoNaLinha}
                    <button className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>

                {/* Resultado do mês — sempre visível, fora do recolher */}
                {resultadoEmBloco}

                {/* Collapsible Content */}
                {!isCollapsed && (
                    <div className="mt-3 pt-3 border-t border-gray-50 dark:border-gray-700/50 flex flex-col sm:flex-row sm:items-center gap-3">
                        <select
                            value={activeChip}
                            onChange={(e) => onSelectChip(e.target.value as WorkChip)}
                            className="w-full sm:w-auto px-3 py-2 rounded-full text-sm font-semibold border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                        >
                            {CHIPS.map(({ key, label }) => (
                                <option key={key} value={key}>
                                    {label}{key === 'favoritos' && favoritosCount > 0 ? ` (${favoritosCount})` : ''}
                                </option>
                            ))}
                        </select>

                        <div className="flex items-center gap-2 shrink-0 sm:ml-auto" title="Meta do dia: 10 visitas concluídas">
                            <Target size={15} className={metaAtingida ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'} />
                            <div className="flex flex-col gap-0.5 min-w-[120px]">
                                <div className="flex items-center justify-between text-[10px] font-semibold">
                                    <span className="text-gray-500 dark:text-gray-400">Visitas hoje</span>
                                    <span className={metaAtingida ? 'text-green-600 dark:text-green-400 font-bold' : 'text-gray-600 dark:text-gray-300 font-bold'}>{counts.meta_hoje}/{META_DIA}</span>
                                </div>
                                <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${metaAtingida ? 'bg-green-500' : 'bg-primary'}`}
                                        style={{ width: `${metaPct}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm px-3 py-2 md:px-4 md:py-3 mb-2 md:mb-3 transition-all duration-300">
            {/* Header row (always visible) */}
            <div 
                className="flex items-center justify-between cursor-pointer select-none"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="shrink-0 flex items-center gap-2">
                    <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        Meu Trabalho
                    </span>
                    {canToggleScope && (
                        <div 
                            className="flex items-center p-0.5 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-700"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => onChangeScope('eu')}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all ${scope === 'eu' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                title="Apenas meus leads"
                            >
                                <User size={11} /> Eu
                            </button>
                            <button
                                onClick={() => onChangeScope('todos')}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all ${scope === 'todos' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                title="Leads de todos"
                            >
                                <Users size={11} /> Todos
                            </button>
                        </div>
                    )}
                </div>

                {resultadoNaLinha}

                <div className="shrink-0 flex items-center gap-2">
                    {/* Collapsed state quick status summary */}
                    {isCollapsed && (
                        <div className="flex items-center gap-2 mr-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            {/* Com o resultado do mês na mesma linha não cabe — os contadores seguem ao abrir a barra */}
                            <span className="hidden sm:inline 2xl:hidden">
                                Termos: {counts.termos_pendentes} | Docs: {counts.pendencias_documentais} | Sem Interação: {counts.sem_interacao}
                            </span>
                            <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                                Visitas: {counts.meta_hoje}/{META_DIA}
                            </span>
                        </div>
                    )}
                    <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
            </div>

            {/* Resultado do mês — sempre visível, fora do recolher */}
            {resultadoEmBloco}

            {/* Collapsible Content */}
            {!isCollapsed && (
                <div className="mt-3 pt-3 border-t border-gray-50 dark:border-gray-700/50 space-y-3">
                    {/* Linha 1: contadores + meta */}
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                        {/* Contadores clicáveis */}
                        <div className="flex items-center gap-2 flex-wrap flex-1">
                            {CONTADORES.map(({ key, label, icon: Icon, tone, activeTone }) => {
                                const active = activeCounter === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => onToggleCounter(key)}
                                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-all whitespace-nowrap
                                            ${active
                                                ? `${activeTone} ring-1 ring-primary/20`
                                                : 'bg-gray-50 dark:bg-gray-700/40 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                                        title={`Filtrar: ${label}`}
                                    >
                                        <Icon size={14} className={tone} />
                                        <span className={`font-bold ${tone}`}>{counterValue(key)}</span>
                                        <span className="text-gray-500 dark:text-gray-400 hidden sm:inline">{label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Medidor de meta do dia */}
                        <div className="flex items-center gap-2 shrink-0 lg:ml-auto" title="Meta do dia: 10 visitas concluídas">
                            <Target size={15} className={metaAtingida ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'} />
                            <div className="flex flex-col gap-0.5 min-w-[120px]">
                                <div className="flex items-center justify-between text-[10px] font-semibold">
                                    <span className="text-gray-500 dark:text-gray-400">Visitas hoje</span>
                                    <span className={metaAtingida ? 'text-green-600 dark:text-green-400 font-bold' : 'text-gray-600 dark:text-gray-300 font-bold'}>{counts.meta_hoje}/{META_DIA}</span>
                                </div>
                                <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${metaAtingida ? 'bg-green-500' : 'bg-primary'}`}
                                        style={{ width: `${metaPct}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Linha 2: chips de filtro inteligente (seleção única) */}
                    <div className="flex items-center gap-1.5 flex-wrap pt-2.5 border-t border-gray-50 dark:border-gray-700/50">
                        {CHIPS.map(({ key, label }) => {
                            const active = activeChip === key;
                            const showCount = key === 'favoritos' && favoritosCount > 0;
                            return (
                                <button
                                    key={key}
                                    onClick={() => onSelectChip(key)}
                                    className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all
                                        ${active
                                            ? 'bg-primary text-white shadow-sm'
                                            : 'bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                                >
                                    {label}{showCount ? ` (${favoritosCount})` : ''}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MeuTrabalhoHoje;
